/**
 * XP Service - Frontend integration with the XP system database layer
 *
 * Responsibility:
 * - Award XP via database RPC (server calculates diminishing returns)
 * - Fetch XP summaries for display (per-language and global)
 * - Fetch XP history for charts
 * - Provide optimistic XP calculations for immediate UI feedback
 * - Detect first-activity-of-day for daily bonus
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  XpEventType,
  XpSourceType,
  XpEvent,
  XpSummary,
  XpHistoryPoint,
  XpEventRow,
  XpSummaryRow,
  XpHistoryRow,
} from '@/types/xp';
import {
  mapXpEventRow,
  mapXpSummaryRow,
  mapXpHistoryRow,
  XP_VALUES,
  calculateDiminishingRate,
  streakMultiplier,
} from '@/types/xp';

export class XpService {
  constructor(private supabase: SupabaseClient) {}

  /**
   * Award XP for a learning activity.
   * The database function handles diminishing returns and validation.
   *
   * @param eventType - Type of XP-worthy activity
   * @param baseXp - Base XP value (before multipliers/diminishing returns)
   * @param multiplier - Streak/accuracy multiplier (default 1.0)
   * @param sourceId - Optional reference to source entity (message ID, card ID, session ID)
   * @param sourceType - Optional source entity type
   * @param metadata - Optional additional context (streak info, accuracy, etc.)
   */
  async awardXp(
    userId: string,
    languageCode: string,
    eventType: XpEventType,
    baseXp: number,
    multiplier: number = 1.0,
    sourceId?: string | null,
    sourceType?: XpSourceType,
    metadata: Record<string, unknown> = {}
  ): Promise<{ data: XpEvent | null; error: string | null }> {
    try {
      const { data, error } = await this.supabase.rpc('award_xp', {
        p_user_id: userId,
        p_language_code: languageCode,
        p_event_type: eventType,
        p_base_xp: baseXp,
        p_multiplier: multiplier,
        p_source_id: sourceId ?? null,
        p_source_type: sourceType ?? null,
        p_metadata: metadata,
      });

      if (error) throw error;
      if (!data) return { data: null, error: 'No event returned from award_xp' };

      return { data: mapXpEventRow(data as XpEventRow), error: null };
    } catch (error) {
      console.error('Error awarding XP:', error);
      return { data: null, error: String(error) };
    }
  }

  /**
   * Fetch XP summary for a specific language (or all languages if null).
   * Returns level, tier, progress, daily/weekly totals.
   */
  async getUserXpSummary(
    userId: string,
    languageCode: string | null = null
  ): Promise<{ data: XpSummary[]; error: string | null }> {
    try {
      const { data, error } = await this.supabase.rpc('get_user_xp_summary', {
        p_user_id: userId,
        p_language_code: languageCode,
      });

      if (error) throw error;
      if (!data) return { data: [], error: null };

      const rows = Array.isArray(data) ? data : [data];
      return {
        data: rows.map((row: XpSummaryRow) => mapXpSummaryRow(row)),
        error: null,
      };
    } catch (error) {
      console.error('Error fetching XP summary:', error);
      return { data: [], error: String(error) };
    }
  }

  /**
   * Fetch XP history for chart rendering (daily aggregates).
   */
  async getXpHistory(
    userId: string,
    languageCode: string,
    days: number = 30
  ): Promise<{ data: XpHistoryPoint[]; error: string | null }> {
    try {
      const { data, error } = await this.supabase.rpc('get_xp_history', {
        p_user_id: userId,
        p_language_code: languageCode,
        p_days: days,
      });

      if (error) throw error;
      if (!data) return { data: [], error: null };

      const rows = Array.isArray(data) ? data : [data];
      return {
        data: rows.map((row: XpHistoryRow) => mapXpHistoryRow(row)),
        error: null,
      };
    } catch (error) {
      console.error('Error fetching XP history:', error);
      return { data: [], error: String(error) };
    }
  }

  /**
   * Get total XP across all languages for global display.
   */
  async getGlobalXpTotal(
    userId: string
  ): Promise<{ data: number; error: string | null }> {
    try {
      // Fetch all language summaries (null = all languages)
      const { data: summaries, error } = await this.getUserXpSummary(userId, null);

      if (error) return { data: 0, error };

      const total = summaries.reduce((sum, s) => sum + s.totalXp, 0);
      return { data: total, error: null };
    } catch (error) {
      console.error('Error fetching global XP total:', error);
      return { data: 0, error: String(error) };
    }
  }

  /**
   * Calculate optimistic XP for immediate UI feedback.
   * Uses client-side constants to approximate what the DB will calculate.
   * The authoritative value comes from the DB on next fetch.
   */
  calculateOptimisticXp(
    eventType: XpEventType,
    xpToday: number,
    streakDays: number = 0
  ): number {
    const baseXp = XP_VALUES[eventType];
    const streak = streakMultiplier(streakDays);
    const rate = calculateDiminishingRate(xpToday);
    return Math.max(1, Math.round(baseXp * streak * rate));
  }

  /**
   * Check if this is the user's first activity today (for daily bonus).
   * Returns true if no XP has been earned today for the given language.
   */
  async isFirstActivityToday(
    userId: string,
    languageCode: string
  ): Promise<boolean> {
    try {
      const { data, error } = await this.supabase
        .from('user_xp')
        .select('xp_today, last_xp_gained_at')
        .eq('user_id', userId)
        .eq('language_code', languageCode)
        .maybeSingle();

      if (error) throw error;

      // No record exists = first activity ever
      if (!data) return true;

      // If last XP was on a different day, it's the first today
      if (!data.last_xp_gained_at) return true;
      const lastDate = new Date(data.last_xp_gained_at).toISOString().split('T')[0];
      const today = new Date().toISOString().split('T')[0];
      return lastDate !== today;
    } catch (error) {
      console.error('Error checking first activity:', error);
      // Default to false to avoid duplicate daily bonuses
      return false;
    }
  }

  /**
   * Award daily bonus if this is the first activity today.
   * Returns the daily bonus event if awarded, null otherwise.
   */
  async awardDailyBonusIfNeeded(
    userId: string,
    languageCode: string
  ): Promise<{ data: XpEvent | null; error: string | null }> {
    const isFirst = await this.isFirstActivityToday(userId, languageCode);
    if (!isFirst) return { data: null, error: null };

    return this.awardXp(
      userId,
      languageCode,
      'daily_bonus',
      XP_VALUES.daily_bonus,
      1.0,
      null,
      null,
      { reason: 'first_activity_of_day' }
    );
  }

  /**
   * Fetch recent XP events for the activity feed.
   * Returns the latest events, most recent first.
   */
  async getRecentXpEvents(
    userId: string,
    languageCode: string,
    limit: number = 20
  ): Promise<{ data: XpEvent[]; error: string | null }> {
    try {
      const { data, error } = await this.supabase
        .from('xp_events')
        .select('*')
        .eq('user_id', userId)
        .eq('language_code', languageCode)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      if (!data) return { data: [], error: null };

      return {
        data: data.map((row: XpEventRow) => mapXpEventRow(row)),
        error: null,
      };
    } catch (error) {
      console.error('Error fetching recent XP events:', error);
      return { data: [], error: String(error) };
    }
  }

  /**
   * Fetch XP summaries for all languages (for per-language breakdown).
   */
  async getAllLanguageXpSummaries(
    userId: string
  ): Promise<{ data: XpSummary[]; error: string | null }> {
    return this.getUserXpSummary(userId, null);
  }

  /**
   * Get the user's current daily XP (for diminishing returns display).
   * Returns 0 if no record or if last activity was a different day.
   */
  async getXpToday(
    userId: string,
    languageCode: string
  ): Promise<number> {
    try {
      const { data, error } = await this.supabase
        .from('user_xp')
        .select('xp_today, last_xp_gained_at')
        .eq('user_id', userId)
        .eq('language_code', languageCode)
        .maybeSingle();

      if (error || !data) return 0;

      // Check if last activity was today
      if (!data.last_xp_gained_at) return 0;
      const lastDate = new Date(data.last_xp_gained_at).toISOString().split('T')[0];
      const today = new Date().toISOString().split('T')[0];
      if (lastDate !== today) return 0;

      return data.xp_today || 0;
    } catch {
      return 0;
    }
  }
}
